import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, Send, Phone, Video, Paperclip, Smile, Check, FileText, Info, Search, ArrowLeft, ShoppingBag, CreditCard, Calendar, Download, X } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { VideoCallModal } from '../ui/VideoCallModal';
import { useLanguage } from '../../i18n/LanguageContext';
interface ChatMessage {
  id: number;
  text: string;
  isOwn: boolean;
  time: string;
  type?: 'text' | 'file' | 'system';
  fileName?: string;
  fileSize?: string;
  systemType?: 'order_created' | 'payment' | 'report_ready' | 'session_scheduled';
}
interface ChatContact {
  id: number;
  name: string;
  initials: string;
  lastMessage: string;
  time: string;
  unread: number;
  online: boolean;
  role: string;
  orderId: string;
  service: string;
  orderStatus: 'in_progress' | 'completed' | 'waiting';
  orderType: 'consultation' | 'report' | 'support';
}
interface ChatTabProps {
  theme: ZodiacTheme;
}
const contacts: ChatContact[] = [{
  id: 0,
  name: 'Елена Смирнова',
  initials: 'ЕС',
  lastMessage: 'Да, завтра в 14:00 мне отлично подходит!',
  time: '14:25',
  unread: 1,
  online: true,
  role: 'Астролог',
  orderId: 'ORD-1052',
  service: 'Синастрия (Совместимость)',
  orderStatus: 'in_progress',
  orderType: 'consultation'
}, {
  id: 1,
  name: 'Служба поддержки',
  initials: 'СП',
  lastMessage: 'Ваш вопрос решён. Спасибо!',
  time: 'Вчера',
  unread: 0,
  online: true,
  role: 'Система',
  orderId: '',
  service: 'Техподдержка',
  orderStatus: 'completed',
  orderType: 'support'
}, {
  id: 2,
  name: 'Мария Звёздная',
  initials: 'МЗ',
  lastMessage: 'Натальная_карта_Анна.pdf',
  time: '10 Мар',
  unread: 0,
  online: false,
  role: 'Астролог',
  orderId: 'ORD-1048',
  service: 'Натальная карта',
  orderStatus: 'completed',
  orderType: 'report'
}];
const initialMessages: Record<number, ChatMessage[]> = {
  0: [{
    id: 1,
    text: 'Заказ ORD-1052 создан',
    isOwn: false,
    time: '10:00',
    type: 'system',
    systemType: 'order_created'
  }, {
    id: 2,
    text: 'Оплата подтверждена',
    isOwn: false,
    time: '10:05',
    type: 'system',
    systemType: 'payment'
  }, {
    id: 3,
    text: 'Здравствуйте, Анна! Я подготовила вашу карту для консультации по синастрии. Подскажите, вам будет удобно созвониться завтра в 14:00?',
    isOwn: false,
    time: '14:20'
  }, {
    id: 4,
    text: 'Добрый день, Елена! Да, завтра в 14:00 мне отлично подходит. Жду ссылку!',
    isOwn: true,
    time: '14:25'
  }, {
    id: 5,
    text: 'Сессия назначена на 15 Октября, 14:00',
    isOwn: false,
    time: '14:30',
    type: 'system',
    systemType: 'session_scheduled'
  }],
  1: [{
    id: 1,
    text: 'Здравствуйте! У меня не отображается купленный отчёт.',
    isOwn: true,
    time: '10:00'
  }, {
    id: 2,
    text: 'Добрый день! Проверили — отчёт доступен в разделе «Мои покупки». Попробуйте обновить страницу.',
    isOwn: false,
    time: '10:15'
  }, {
    id: 3,
    text: 'Да, теперь вижу. Спасибо!',
    isOwn: true,
    time: '10:20'
  }, {
    id: 4,
    text: 'Ваш вопрос решён. Спасибо за обращение!',
    isOwn: false,
    time: '10:22'
  }],
  2: [{
    id: 1,
    text: 'Заказ ORD-1048 создан',
    isOwn: false,
    time: '08:50',
    type: 'system',
    systemType: 'order_created'
  }, {
    id: 2,
    text: 'Оплата подтверждена',
    isOwn: false,
    time: '08:55',
    type: 'system',
    systemType: 'payment'
  }, {
    id: 3,
    text: 'Здравствуйте! Заказала натальную карту, когда будет готова?',
    isOwn: true,
    time: '09:00'
  }, {
    id: 4,
    text: 'Добрый день! Отчёт готов, отправляю файл.',
    isOwn: false,
    time: '12:30'
  }, {
    id: 5,
    text: 'Отчёт готов к скачиванию',
    isOwn: false,
    time: '12:31',
    type: 'system',
    systemType: 'report_ready'
  }, {
    id: 6,
    text: 'Натальная_карта_Анна.pdf',
    isOwn: false,
    time: '12:31',
    type: 'file',
    fileName: 'Натальная_карта_Анна.pdf',
    fileSize: '3.1 MB'
  }]
};
const commonEmojis = ['😊', '👍', '❤️', '🙏', '✨', '🔮', '⭐', '💫', '🌙', '☀️', '🎯', '✅', '❌', '🤔', '😂', '🥰', '💜', '🙌', '👏', '💡'];
const SYSTEM_ICONS: Record<string, React.ReactNode> = {
  order_created: <ShoppingBag className="w-3.5 h-3.5 text-indigo-500" data-id="element-18113" />,
  payment: <CreditCard className="w-3.5 h-3.5 text-emerald-500" data-id="element-18114" />,
  report_ready: <FileText className="w-3.5 h-3.5 text-blue-500" data-id="element-18115" />,
  session_scheduled: <Calendar className="w-3.5 h-3.5 text-amber-500" data-id="element-18116" />
};
export function ChatTab({
  theme
}: ChatTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const [showCallModal, setShowCallModal] = useState(false);
  const [callMode, setCallMode] = useState<'video' | 'audio'>('video');
  const [activeChat, setActiveChat] = useState(0);
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');
  const [chatMessage, setChatMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [messagesMap, setMessagesMap] = useState<Record<number, ChatMessage[]>>(initialMessages);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const currentMessages = messagesMap[activeChat] || [];
  const currentContact = contacts[activeChat];
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [currentMessages, activeChat]);
  const handleSend = () => {
    if (!chatMessage.trim()) return;
    const newMsg: ChatMessage = {
      id: Date.now(),
      text: chatMessage,
      isOwn: true,
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
    setMessagesMap(prev => ({
      ...prev,
      [activeChat]: [...(prev[activeChat] || []), newMsg]
    }));
    setChatMessage('');
  };
  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const newMsg: ChatMessage = {
      id: Date.now(),
      text: file.name,
      isOwn: true,
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      type: 'file',
      fileName: file.name,
      fileSize: `${(file.size / 1024).toFixed(1)} KB`
    };
    setMessagesMap(prev => ({
      ...prev,
      [activeChat]: [...(prev[activeChat] || []), newMsg]
    }));
    e.target.value = '';
    showToast(t('userdash.chat.fileAttached', {
      name: file.name
    }));
  };
  const insertEmoji = (emoji: string) => {
    setChatMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };
  const filteredContacts = contacts.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.service.toLowerCase().includes(searchQuery.toLowerCase()));
  const selectContact = (id: number) => {
    setActiveChat(id);
    setMobileView('chat');
    setShowProfile(false);
    setShowEmojiPicker(false);
  };
  const OrderStatusBadge = ({
    status
  }: {
    status: string;
  }) => {
    if (status === 'in_progress') return <Badge variant="warning" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-18117">
          {t('userdash.chat.statusInProgress')}
        </Badge>;
    if (status === 'completed') return <Badge variant="success" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-18118">
          {t('userdash.chat.statusCompleted')}
        </Badge>;
    if (status === 'waiting') return <Badge variant="info" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-18119">
          {t('userdash.chat.statusWaiting')}
        </Badge>;
    return null;
  };
  // ── CONTACT LIST ──
  const ContactList = () => <div className={`w-full md:w-80 border-r border-slate-200 flex-col bg-white ${mobileView === 'chat' ? 'hidden md:flex' : 'flex'}`} data-id="element-18120">
      <div className="p-3 border-b border-slate-100" data-id="element-18121">
        <div className="relative" data-id="element-18122">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-18123" />
          <input type="text" placeholder={t('userdash.chat.search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50" data-id="element-18124" />
        </div>
      </div>
      <div className="overflow-y-auto flex-grow" data-id="element-18125">
        {filteredContacts.length > 0 ? filteredContacts.map(contact => <div key={contact.id} onClick={() => selectContact(contact.id)} className={`p-3 border-b border-slate-50 cursor-pointer transition-colors ${activeChat === contact.id ? 'bg-indigo-50/60' : 'hover:bg-slate-50'}`} data-id="element-18126">
              <div className="flex items-start gap-3" data-id="element-18127">
                <div className="relative shrink-0 mt-0.5" data-id="element-18128">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm" data-id="element-18129">
                    {contact.initials}
                  </div>
                  {contact.online && <div className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" data-id="element-18130" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-18131">
                  <div className="flex justify-between items-baseline mb-0.5" data-id="element-18132">
                    <h4 className="font-bold text-slate-900 truncate text-sm" data-id="element-18133">
                      {contact.name}
                    </h4>
                    <span className="text-[10px] text-slate-400 shrink-0 ml-2" data-id="element-18134">
                      {contact.time}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mb-1" data-id="element-18135">
                    <span className="text-[10px] text-slate-500 truncate" data-id="element-18136">
                      {contact.service}
                    </span>
                    <OrderStatusBadge status={contact.orderStatus} data-id="element-18137" />
                  </div>
                  <div className="flex justify-between items-center" data-id="element-18138">
                    <p className="text-xs text-slate-500 truncate" data-id="element-18139">
                      {contact.lastMessage}
                    </p>
                    {contact.unread > 0 && <span className={`ml-2 w-5 h-5 rounded-full bg-gradient-to-r ${theme.gradient} text-white text-[10px] font-bold flex items-center justify-center shrink-0`} data-id="element-18140">
                        {contact.unread}
                      </span>}
                  </div>
                </div>
              </div>
            </div>) : <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-id="element-18141">
            <MessageSquare className="w-12 h-12 text-slate-200 mb-3" data-id="element-18142" />
            <p className="text-sm text-slate-500 mb-4" data-id="element-18143">
              {t('userdash.chat.noMessages')}
            </p>
            <p className="text-xs text-slate-400 mb-5 max-w-[200px]" data-id="element-18144">
              {t('userdash.chat.noMessagesDesc')}
            </p>
            <Button variant="primary" size="sm" onClick={() => showToast(t('userdash.chat.goToCatalog'))} data-id="element-18145">
              {t('userdash.chat.findAstrologer')}
            </Button>
          </div>}
      </div>
    </div>;
  // ── CHAT AREA ──
  const ChatArea = () => <div className={`flex-1 flex-col bg-slate-50 ${mobileView === 'list' ? 'hidden md:flex' : 'flex'}`} data-id="element-18146">
      {/* Header */}
      <div className="p-3 border-b border-slate-200 bg-white" data-id="element-18147">
        <div className="flex justify-between items-center" data-id="element-18148">
          <div className="flex items-center gap-3" data-id="element-18149">
            <button onClick={() => setMobileView('list')} className="md:hidden p-1.5 -ml-1 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors" data-id="element-18150">
              <ArrowLeft className="w-5 h-5" data-id="element-18151" />
            </button>
            <div className="relative" data-id="element-18152">
              <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm" data-id="element-18153">
                {currentContact.initials}
              </div>
              {currentContact.online && <div className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-emerald-500 border-2 border-white" data-id="element-18154" />}
            </div>
            <div data-id="element-18155">
              <h3 className="font-bold text-slate-900 text-sm" data-id="element-18156">
                {currentContact.name}
              </h3>
              <div className="text-[11px] flex items-center gap-1" data-id="element-18157">
                {currentContact.online ? <span className="text-emerald-500" data-id="element-18158">
                    {t('userdash.chat.online')}
                  </span> : <span className="text-slate-400" data-id="element-18159">
                    {t('userdash.chat.offline')}
                  </span>}
                <span className="text-slate-300" data-id="element-18160">·</span>
                <span className="text-slate-400" data-id="element-18161">
                  {currentContact.role === 'Астролог' ? t('userdash.chat.roleAstrologer') : t('userdash.chat.roleSystem')}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1" data-id="element-18162">
            {currentContact.orderType !== 'support' && <>
                <button onClick={() => {
              setCallMode('audio');
              setShowCallModal(true);
            }} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" title={t('userdash.chat.audioCall')} data-id="element-18163">
                  <Phone className="w-4.5 h-4.5" data-id="element-18164" />
                </button>
                <button onClick={() => {
              setCallMode('video');
              setShowCallModal(true);
            }} className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors" title={t('userdash.chat.videoCall')} data-id="element-18165">
                  <Video className="w-4.5 h-4.5" data-id="element-18166" />
                </button>
              </>}
            <button onClick={() => setShowProfile(!showProfile)} className={`p-2 rounded-lg transition-colors ${showProfile ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`} title={t('userdash.chat.info')} data-id="element-18167">
              <Info className="w-4.5 h-4.5" data-id="element-18168" />
            </button>
          </div>
        </div>
      </div>

      {/* Order Info Bar */}
      {currentContact.orderId && <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2" data-id="element-18169">
          <div className="flex items-center gap-2 min-w-0" data-id="element-18170">
            <ShoppingBag className="w-3.5 h-3.5 text-slate-400 shrink-0" data-id="element-18171" />
            <span className="text-xs text-slate-600 truncate" data-id="element-18172">
              <span className="font-medium" data-id="element-18173">{currentContact.orderId}</span>
              <span className="text-slate-400 mx-1" data-id="element-18174">·</span>
              {currentContact.service}
            </span>
            <OrderStatusBadge status={currentContact.orderStatus} data-id="element-18175" />
          </div>
          <div className="flex items-center gap-1.5 shrink-0" data-id="element-18176">
            {currentContact.orderType === 'consultation' && currentContact.orderStatus === 'in_progress' && <Button variant="primary" size="sm" className="text-[11px] h-7 px-2.5 bg-indigo-600 hover:bg-indigo-700" onClick={() => showToast(t('userdash.chat.waitForLink'))} data-id="element-18177">
                  {t('userdash.chat.joinCall')}
                </Button>}
            {currentContact.orderType === 'report' && currentContact.orderStatus === 'completed' && <Button variant="secondary" size="sm" className="text-[11px] h-7 px-2.5" onClick={() => showToast(t('userdash.chat.downloading'))} data-id="element-18178">
                  <Download className="w-3.5 h-3.5 mr-1" data-id="element-18179" />{' '}
                  {t('userdash.chat.download')}
                </Button>}
            <Button variant="outline" size="sm" className="text-[11px] h-7 px-2.5" onClick={() => showToast(t('userdash.chat.goToOrder', {
          id: currentContact.orderId
        }))} data-id="element-18180">
              {t('userdash.chat.openOrder')}
            </Button>
          </div>
        </div>}

      {/* Profile Panel */}
      {showProfile && <div className="px-4 py-3 bg-indigo-50/50 border-b border-indigo-100 flex items-center gap-4" data-id="element-18181">
          <div className="w-11 h-11 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-base shrink-0" data-id="element-18182">
            {currentContact.initials}
          </div>
          <div className="flex-1 min-w-0" data-id="element-18183">
            <div className="font-bold text-slate-900 text-sm" data-id="element-18184">
              {currentContact.name}
            </div>
            <div className="text-xs text-slate-500" data-id="element-18185">
              {currentContact.role === 'Астролог' ? t('userdash.chat.roleAstrologer') : t('userdash.chat.roleSystem')}{' '}
              ·{' '}
              {currentContact.online ? t('userdash.chat.online') : t('userdash.chat.offline')}
            </div>
            {currentContact.orderId && <div className="text-[10px] text-slate-400 mt-0.5" data-id="element-18186">
                {currentContact.service} · {currentContact.orderId}
              </div>}
          </div>
          <button onClick={() => setShowProfile(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-white transition-colors shrink-0" data-id="element-18187">
            <X className="w-4 h-4" data-id="element-18188" />
          </button>
        </div>}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-grow overflow-y-auto p-4 space-y-3" data-id="element-18189">
        <div className="flex justify-center my-1" data-id="element-18190">
          <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-3 py-1 rounded-full" data-id="element-18191">
            — {t('userdash.chat.today')} —
          </span>
        </div>

        {currentMessages.map(msg => {
        // System message
        if (msg.type === 'system') {
          return <div key={msg.id} className="flex justify-center my-2" data-id="element-18192">
                <div className="flex items-center gap-1.5 bg-slate-100 text-slate-500 text-[11px] font-medium px-3 py-1.5 rounded-full" data-id="element-18193">
                  {SYSTEM_ICONS[msg.systemType || ''] || <Info className="w-3.5 h-3.5 text-slate-400" data-id="element-18194" />}
                  <span data-id="element-18195">{msg.text}</span>
                </div>
              </div>;
        }
        // Regular / file message
        return <div key={msg.id} className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`} data-id="element-18196">
              <div className="flex items-end gap-1.5 max-w-[80%]" data-id="element-18197">
                {!msg.isOwn && <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-[9px] shrink-0 mb-1" data-id="element-18198">
                    {currentContact.initials}
                  </div>}
                <div data-id="element-18199">
                  {msg.type === 'file' ? <div className={`flex items-center gap-2.5 px-3 py-2 rounded-2xl cursor-pointer ${msg.isOwn ? `bg-gradient-to-r ${theme.gradient} text-white rounded-tr-none` : 'bg-white border border-slate-200 text-slate-800 rounded-tl-none shadow-sm'}`} onClick={() => showToast(t('userdash.chat.downloadingFile', {
                name: msg.fileName
              }))} data-id="element-18200">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${msg.isOwn ? 'bg-white/20' : 'bg-indigo-50'}`} data-id="element-18201">
                        <FileText className={`w-4 h-4 ${msg.isOwn ? 'text-white' : 'text-indigo-600'}`} data-id="element-18202" />
                      </div>
                      <div className="min-w-0" data-id="element-18203">
                        <div className="text-sm font-medium truncate" data-id="element-18204">
                          {msg.fileName}
                        </div>
                        <div className={`text-[10px] ${msg.isOwn ? 'text-white/70' : 'text-slate-400'}`} data-id="element-18205">
                          {msg.fileSize}
                        </div>
                      </div>
                    </div> : <div className={`px-3 py-2 rounded-2xl ${msg.isOwn ? `bg-gradient-to-r ${theme.gradient} text-white rounded-tr-none` : 'bg-white border border-slate-200 text-slate-800 rounded-tl-none shadow-sm'}`} data-id="element-18206">
                      <p className="text-sm leading-relaxed" data-id="element-18207">{msg.text}</p>
                    </div>}
                  <div className={`flex items-center gap-1 mt-0.5 ${msg.isOwn ? 'justify-end mr-1' : 'ml-1'}`} data-id="element-18208">
                    <span className="text-[10px] text-slate-400" data-id="element-18209">
                      {msg.time}
                    </span>
                    {msg.isOwn && <Check className="w-3 h-3 text-indigo-400" data-id="element-18210" />}
                  </div>
                </div>
                {msg.isOwn && <div className={`w-6 h-6 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center font-bold text-[9px] shrink-0 mb-1`} data-id="element-18211">
                    А
                  </div>}
              </div>
            </div>;
      })}
        <div ref={messagesEndRef} data-id="element-18212" />
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-slate-200" data-id="element-18213">
        <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileAttach} data-id="element-18214" />

        {showEmojiPicker && <div className="px-3 pt-3" data-id="element-18215">
            <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-18216">
              <div className="flex justify-between items-center mb-2" data-id="element-18217">
                <span className="text-xs font-bold text-slate-700" data-id="element-18218">
                  {t('userdash.chat.emoji')}
                </span>
                <button onClick={() => setShowEmojiPicker(false)} className="text-slate-400 hover:text-slate-600" data-id="element-18219">
                  <X className="w-3.5 h-3.5" data-id="element-18220" />
                </button>
              </div>
              <div className="grid grid-cols-10 gap-0.5" data-id="element-18221">
                {commonEmojis.map(emoji => <button key={emoji} onClick={() => insertEmoji(emoji)} className="w-8 h-8 flex items-center justify-center text-lg hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-18222">
                    {emoji}
                  </button>)}
              </div>
            </div>
          </div>}

        <div className="p-3 flex items-center gap-2" data-id="element-18223">
          <button onClick={() => fileInputRef.current?.click()} className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg hover:bg-slate-100 transition-colors shrink-0" title={t('userdash.chat.attachFile')} data-id="element-18224">
            <Paperclip className="w-5 h-5" data-id="element-18225" />
          </button>
          <input type="text" placeholder={t('userdash.chat.typeMessage')} value={chatMessage} onChange={e => setChatMessage(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey && chatMessage.trim()) {
            e.preventDefault();
            handleSend();
          }
        }} className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-18226" />
          <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} className={`p-2 rounded-lg transition-colors shrink-0 ${showEmojiPicker ? 'text-amber-500 bg-amber-50' : 'text-slate-400 hover:text-amber-500 hover:bg-slate-100'}`} title={t('userdash.chat.emoji')} data-id="element-18227">
            <Smile className="w-5 h-5" data-id="element-18228" />
          </button>
          <button onClick={handleSend} disabled={!chatMessage.trim()} className={`w-10 h-10 rounded-full bg-gradient-to-r ${theme.gradient} text-white flex items-center justify-center transition-opacity hover:opacity-90 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed`} data-id="element-18229">
            <Send className="w-4 h-4 ml-0.5" data-id="element-18230" />
          </button>
        </div>
      </div>
    </div>;
  return <div className="h-[calc(100vh-8rem)] sm:h-[calc(100vh-12rem)] min-h-[500px] sm:min-h-[600px] flex flex-col" data-id="element-18231">
      <div className="flex justify-between items-center mb-4" data-id="element-18232">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-18233">
          {t('userdash.chat.title')}
        </h2>
      </div>

      <Card padding="none" className="flex-grow flex overflow-hidden border border-slate-200 shadow-sm" data-id="element-18234">
        <ContactList data-id="element-18235" />
        <ChatArea data-id="element-18236" />
      </Card>

      <VideoCallModal isOpen={showCallModal} onClose={() => setShowCallModal(false)} mode={callMode} participantName={currentContact.name} participantInitials={currentContact.initials} onEnd={() => setShowCallModal(false)} data-id="element-18237" />
    </div>;
}